import { useEffect, useMemo, useState } from 'react';
import {
  askAi,
  askAiBrief,
  decideAiProposal,
  getAiResponseEnvelope,
  listAiEvals,
  listAiProposals,
  listAiRuns,
  proposeAiAction,
  submitAiFeedback,
} from '../../api/ai';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  GroupLabel,
  HintNote,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Select,
  StatusBadge,
  formatDateTime,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
  Pagination,
} from '../../components/common';
import type { AiEval, AiProposal, AiProposalActionType, AiResponseEnvelope, AiResultConfidence, AiRun, UUID } from '../../types/domain';

const TABS = ['chat', 'brief', 'proposals', 'evals'] as const;
type Tab = (typeof TABS)[number];
const TAB_LABEL: Record<Tab, string> = { chat: 'Tanya AI', brief: 'Ringkasan Harian', proposals: 'Usulan Tindakan', evals: 'Uji Keamanan AI' };

/**
 * AI Analyst (FASE 14, migration 0055; corrected in migration 0056). Every
 * answer here comes from the real internal DEV_PROVIDER — a deterministic
 * keyword→intent→tool rule engine, NOT a language model. It only ever
 * echoes fields it read from a real tool call (attendance, stock, purchase
 * orders, finance P&L, report drafts, registered metrics, branch stock
 * summary); it never invents a number. There is no real LLM provider
 * credential wired up anywhere in this build — production LLM-backed
 * generation is BLOCKED_BY_PROVIDER.
 *
 * AI never writes ERP data directly. Every mutation-intent question can
 * only ever produce a PENDING proposal here — approving one re-checks the
 * real domain permission and calls an already-accepted canonical domain
 * function, exactly as if a human had used the normal page.
 *
 * Both `/ai/query` and the Daily Owner Brief return the SAME literal
 * structured envelope (answer/confidence/evidence/warnings/proposals/
 * data_timestamp/scope/tool_refs/ai_run_id) — `AiRunDrawer` below renders
 * that envelope directly, not a client-side reassembly of it.
 */
export default function AiAnalystPage() {
  const { hasPermission } = usePermissions();
  const [tab, setTab] = useState<Tab>('chat');

  return (
    <div>
      <PageHeader
        title="AI Analyst"
        subtitle="Dijawab oleh DEV_PROVIDER internal (aturan deterministik, bukan model bahasa) — hanya membaca hasil tool nyata, tidak pernah mengarang angka. Belum ada kredensial penyedia AI produksi (BLOCKED_BY_PROVIDER)."
      />
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <Button key={t} variant={tab === t ? 'primary' : 'secondary'} onClick={() => setTab(t)} style={{ padding: '0 14px' }}>
            {TAB_LABEL[t]}
          </Button>
        ))}
      </div>

      {tab === 'chat' && <ChatTab canApprove={hasPermission('ai', 'approve')} canFeedback={hasPermission('ai', 'feedback')} />}
      {tab === 'brief' && <BriefTab canQuery={hasPermission('ai', 'query')} canApprove={hasPermission('ai', 'approve')} canFeedback={hasPermission('ai', 'feedback')} />}
      {tab === 'proposals' && <ProposalsTab canApprove={hasPermission('ai', 'approve')} />}
      {tab === 'evals' && <EvalsTab />}
    </div>
  );
}

const CONFIDENCE_LABEL: Record<AiResultConfidence, string> = {
  GROUNDED: 'Berdasar Data Nyata',
  PARTIAL: 'Sebagian',
  UNAVAILABLE: 'Data Tidak Tersedia',
};

const ACTION_TYPE_LABEL: Record<AiProposalActionType, string> = {
  MAINTENANCE_ORDER_SUGGESTION: 'Usulan Perintah Maintenance',
  REORDER_SUGGESTION: 'Usulan Pemesanan Ulang Stok',
  ATTENDANCE_INVESTIGATION_SUGGESTION: 'Usulan Investigasi Kehadiran',
  FINANCE_VARIANCE_REVIEW_SUGGESTION: 'Usulan Tinjauan Varians Keuangan',
};

function ChatTab({ canApprove, canFeedback }: { canApprove: boolean; canFeedback: boolean }) {
  const { activeBranchId } = useBranch();
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runs, setRuns] = useState<AiRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRunId, setSelectedRunId] = useState<UUID | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setRuns(await listAiRuns());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const ask = async () => {
    if (!question.trim()) {
      setError('Ketik pertanyaan terlebih dahulu.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // fn_ai_query_checked returns the full structured envelope directly
      // (migration 0056) — envelope.ai_run_id is all this handler needs;
      // the history table below still comes from listAiRuns() (question
      // text/intent/confidence columns), refreshed after the ask.
      const envelope = await askAi({ branchId: activeBranchId, question: question.trim(), idempotencyKey: crypto.randomUUID() });
      setQuestion('');
      await load();
      setSelectedRunId(envelope.ai_run_id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="alex-card" style={{ padding: 16, marginBottom: 16 }}>
        <FieldLabel label="Pertanyaan (contoh: 'Kenapa keterlambatan meningkat?', 'stok item berapa', 'status purchase order', 'buat laporan harian')">
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Tanyakan sesuatu tentang bisnis Anda..."
            rows={2}
            style={{
              display: 'block',
              width: '100%',
              padding: '8px 10px',
              border: '1px solid var(--alex-border)',
              borderRadius: 'var(--alex-control-radius)',
              fontSize: 'var(--alex-fs-caption)',
              color: 'var(--alex-text-primary)',
              background: 'var(--alex-surface)',
              fontFamily: 'var(--alex-font-family)',
              resize: 'vertical',
            }}
          />
        </FieldLabel>
        <div style={{ marginTop: 10 }}>
          <Button variant="primary" loading={busy} onClick={() => void ask()}>
            Tanya
          </Button>
        </div>
        {error && <ErrorNote text={error} />}
      </div>

      {loading && <LoadingNote />}
      {!loading && runs.length === 0 && <EmptyState text="Belum ada pertanyaan yang diajukan." />}
      {!loading && runs.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Pertanyaan</th>
              <th style={thStyle}>Maksud</th>
              <th style={thStyle}>Keyakinan</th>
              <th style={thStyle}>Waktu</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <ClickableRow key={r.ai_run_id} onClick={() => setSelectedRunId(r.ai_run_id)}>
                <td style={tdStyle}>{r.question_text}</td>
                <td style={tdStyle}>{r.intent}</td>
                <td style={tdStyle}>
                  {r.result_confidence && <StatusBadge status={CONFIDENCE_LABEL[r.result_confidence]} />}
                  {!r.result_confidence && <StatusBadge status={r.status} />}
                </td>
                <td style={tdStyle}>{formatDateTime(r.started_at)}</td>
              </ClickableRow>
            ))}
          </tbody>
        </table>
      )}

      <AiRunDrawer
        aiRunId={selectedRunId}
        title="AI — Jawaban"
        open={!!selectedRunId}
        onClose={() => setSelectedRunId(null)}
        canApprove={canApprove}
        canFeedback={canFeedback}
      />
    </div>
  );
}

function BriefTab({ canQuery, canApprove, canFeedback }: { canQuery: boolean; canApprove: boolean; canFeedback: boolean }) {
  const { activeBranchId } = useBranch();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<UUID | null>(null);
  const [lastGeneratedAt, setLastGeneratedAt] = useState<string | null>(null);

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      // Fresh idempotency key per click — each click is a new, intentional
      // on-demand brief request, not a retry of a prior one.
      const envelope = await askAiBrief({ branchId: activeBranchId, idempotencyKey: crypto.randomUUID() });
      setSelectedRunId(envelope.ai_run_id);
      setLastGeneratedAt(new Date().toISOString());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="alex-card" style={{ padding: 16, marginBottom: 16 }}>
        <p style={{ fontSize: 12, color: 'var(--alex-text-secondary)', marginTop: 0 }}>
          Ringkasan disusun sesuai permintaan (on-demand) dari kehadiran, purchase order terbuka, stok cabang, dan laba rugi periode berjalan —
          hanya bagian yang benar-benar sesuai izin dan tersedia datanya yang ditampilkan; bagian lain dinyatakan jujur "DATA TIDAK TERSEDIA".
        </p>
        <HintNote text="Penjadwalan otomatis harian BLOCKED_BY_INFRASTRUCTURE — tidak ada cron/worker di build ini. Gunakan tombol di bawah untuk membuat ringkasan sesuai permintaan." />
        {!canQuery && <HintNote text="Peran Anda tidak memiliki izin ai.query untuk membuat ringkasan." />}
        {canQuery && (
          <div style={{ marginTop: 12 }}>
            <Button variant="primary" loading={busy} onClick={() => void generate()}>
              Buat Ringkasan Sekarang
            </Button>
            {lastGeneratedAt && (
              <span style={{ marginLeft: 10, fontSize: 11.5, color: 'var(--alex-text-muted)' }}>
                Terakhir dibuat: {formatDateTime(lastGeneratedAt)}
              </span>
            )}
          </div>
        )}
        {error && <ErrorNote text={error} />}
      </div>

      {!selectedRunId && <EmptyState text="Belum ada ringkasan yang dibuat pada sesi ini." />}

      <AiRunDrawer
        aiRunId={selectedRunId}
        title="Ringkasan Pemilik Harian"
        open={!!selectedRunId}
        onClose={() => setSelectedRunId(null)}
        canApprove={canApprove}
        canFeedback={canFeedback}
      />
    </div>
  );
}

/**
 * Renders the literal AiResponseEnvelope returned by fn_ai_query_checked /
 * fn_ai_brief_checked — fetched fresh via fn_ai_get_response_envelope_checked
 * (migration 0056), never reassembled client-side from separate calls.
 * FACT vs AI INTERPRETATION distinction: "FAKTA" = evidence[] (verbatim
 * tool results); "answer" is the AI's composed interpretation OF those
 * facts, labeled as such and never shown without the facts beneath it.
 */
function AiRunDrawer({
  aiRunId,
  title,
  open,
  onClose,
  canApprove,
  canFeedback,
}: {
  aiRunId: UUID | null;
  title: string;
  open: boolean;
  onClose: () => void;
  canApprove: boolean;
  canFeedback: boolean;
}) {
  const [envelope, setEnvelope] = useState<AiResponseEnvelope | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedbackSent, setFeedbackSent] = useState<string | null>(null);
  const [proposeType, setProposeType] = useState<AiProposalActionType>('MAINTENANCE_ORDER_SUGGESTION');
  const [proposeReason, setProposeReason] = useState('');
  const [proposeBusy, setProposeBusy] = useState(false);
  const [proposeResult, setProposeResult] = useState<string | null>(null);

  const reload = (id: UUID) => {
    setLoading(true);
    setError(null);
    getAiResponseEnvelope(id)
      .then(setEnvelope)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!aiRunId) return;
    setFeedbackSent(null);
    setProposeResult(null);
    reload(aiRunId);
  }, [aiRunId]);

  if (!aiRunId) return null;

  const sendFeedback = async (type: 'HELPFUL' | 'NOT_HELPFUL') => {
    try {
      await submitAiFeedback(aiRunId, type);
      setFeedbackSent(type === 'HELPFUL' ? 'Terima kasih atas masukan Anda (Membantu).' : 'Terima kasih atas masukan Anda (Kurang Membantu).');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const submitProposal = async () => {
    if (!proposeReason.trim()) {
      setError('Isi alasan usulan tindakan.');
      return;
    }
    setProposeBusy(true);
    setError(null);
    try {
      const id = await proposeAiAction({
        aiRunId,
        actionType: proposeType,
        targetResourceType: 'manual',
        reason: proposeReason.trim(),
        idempotencyKey: crypto.randomUUID(),
      });
      setProposeResult(`Usulan dibuat (belum dieksekusi apa pun) — proposal_id: ${id.slice(0, 8)}... Tinjau & setujui di tab "Usulan Tindakan".`);
      setProposeReason('');
      reload(aiRunId); // envelope.proposals[] now reflects the new proposal
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setProposeBusy(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title={title}>
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}

      {!loading && envelope && (
        <>
          <DrawerField
            label="Jawaban (interpretasi AI atas FAKTA di bawah)"
            value={
              <div>
                <div>{envelope.answer ?? '—'}</div>
                {envelope.confidence && (
                  <div style={{ marginTop: 4 }}>
                    <StatusBadge status={CONFIDENCE_LABEL[envelope.confidence]} />
                  </div>
                )}
              </div>
            }
          />
          {envelope.data_timestamp && <DrawerField label="Data per (bukan waktu jawaban)" value={formatDateTime(envelope.data_timestamp)} />}
          <DrawerField
            label="Cakupan (scope)"
            value={
              <span style={{ fontSize: 11 }}>
                tenant <code>{envelope.scope.tenant_id.slice(0, 8)}…</code> · cabang{' '}
                {envelope.scope.branch_id ? <code>{envelope.scope.branch_id.slice(0, 8)}…</code> : 'seluruh tenant'}
              </span>
            }
          />
          <DrawerField label="ai_run_id" value={<code style={{ fontSize: 11 }}>{envelope.ai_run_id}</code>} />

          {envelope.warnings.length > 0 && (
            <div style={{ marginTop: 10 }}>
              {envelope.warnings.map((w, i) => (
                <ErrorNote key={i} text={w} />
              ))}
            </div>
          )}

          {canFeedback && (
            <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
              <Button variant="secondary" onClick={() => void sendFeedback('HELPFUL')} style={{ padding: '0 12px' }}>
                Membantu
              </Button>
              <Button variant="secondary" onClick={() => void sendFeedback('NOT_HELPFUL')} style={{ padding: '0 12px' }}>
                Kurang Membantu
              </Button>
              {feedbackSent && <span style={{ fontSize: 11.5, color: 'var(--alex-success)' }}>{feedbackSent}</span>}
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <GroupLabel>FAKTA (Bukti Tool yang Dipanggil — evidence[])</GroupLabel>
            {envelope.evidence.length === 0 && (
              <EmptyState text="Tidak ada tool yang dipanggil (pertanyaan tidak cocok dengan tool manapun)." variant="section" />
            )}
            {envelope.evidence.length > 0 && (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Tool</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Waktu</th>
                  </tr>
                </thead>
                <tbody>
                  {envelope.evidence.map((tc) => (
                    <tr key={tc.tool_call_id}>
                      <td style={tdStyle}>
                        <code style={{ fontSize: 11 }}>{tc.tool_name}</code>
                      </td>
                      <td style={tdStyle}>
                        <StatusBadge status={tc.status} />
                        {tc.error && <div style={{ fontSize: 10.5, color: 'var(--alex-text-muted)', marginTop: 2 }}>{tc.error}</div>}
                      </td>
                      <td style={tdStyle}>{formatDateTime(tc.completed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {envelope.proposals.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <GroupLabel>Usulan Tindakan dari Sesi Ini (proposals[])</GroupLabel>
              {envelope.proposals.map((p) => (
                <div key={p.proposal_id} style={{ padding: '6px 0', borderBottom: '1px solid var(--alex-border-soft)', fontSize: 11.5 }}>
                  {ACTION_TYPE_LABEL[p.action_type]} · <StatusBadge status={PROPOSAL_STATUS_LABEL[p.status]} />
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--alex-border-soft)' }}>
            <GroupLabel>Buat Usulan Tindakan (tidak langsung mengeksekusi apa pun)</GroupLabel>
            <p style={{ fontSize: 11, color: 'var(--alex-text-muted)', marginTop: 0 }}>
              Hanya membuat baris PENDING di ai_proposals. Eksekusi nyata (jika ada) hanya terjadi setelah pemegang izin ai.approve
              menyetujuinya di tab "Usulan Tindakan" — dan hanya untuk jenis Maintenance/Pemesanan Ulang yang benar-benar memanggil fungsi
              domain kanonik.
            </p>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ width: 240 }}>
                <FieldLabel label="Jenis Usulan">
                  <Select value={proposeType} onChange={(e) => setProposeType(e.target.value as AiProposalActionType)}>
                    {(Object.keys(ACTION_TYPE_LABEL) as AiProposalActionType[]).map((k) => (
                      <option key={k} value={k}>
                        {ACTION_TYPE_LABEL[k]}
                      </option>
                    ))}
                  </Select>
                </FieldLabel>
              </div>
              <div style={{ width: 280 }}>
                <FieldLabel label="Alasan">
                  <input
                    value={proposeReason}
                    onChange={(e) => setProposeReason(e.target.value)}
                    placeholder="mis. keterlambatan meningkat, perlu ditindaklanjuti"
                    style={{
                      display: 'block',
                      width: '100%',
                      height: 'var(--alex-control-height)',
                      padding: '0 10px',
                      border: '1px solid var(--alex-border)',
                      borderRadius: 'var(--alex-control-radius)',
                      fontSize: 'var(--alex-fs-caption)',
                      background: 'var(--alex-surface)',
                      fontFamily: 'var(--alex-font-family)',
                    }}
                  />
                </FieldLabel>
              </div>
              <Button variant="primary" loading={proposeBusy} onClick={() => void submitProposal()}>
                Buat Usulan
              </Button>
            </div>
            {proposeResult && <p style={{ fontSize: 11.5, color: 'var(--alex-success)' }}>{proposeResult}</p>}
            {!canApprove && <HintNote text="Peran Anda dapat membuat usulan, tetapi memerlukan izin ai.approve untuk menyetujuinya." />}
          </div>
        </>
      )}
    </Drawer>
  );
}

const PROPOSAL_STATUS_LABEL: Record<AiProposal['status'], string> = {
  PENDING: 'Menunggu Persetujuan',
  REJECTED: 'Ditolak',
  EXPIRED: 'Kedaluwarsa',
  EXECUTED: 'Dieksekusi',
  EXECUTION_FAILED: 'Eksekusi Gagal',
};

function ProposalsTab({ canApprove }: { canApprove: boolean }) {
  const [rows, setRows] = useState<AiProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'ALL' | AiProposal['status']>('ALL');
  const [selected, setSelected] = useState<AiProposal | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listAiProposals());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(() => rows.filter((r) => statusFilter === 'ALL' || r.status === statusFilter), [rows, statusFilter]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);

  return (
    <div>
      {!canApprove && <HintNote text="Peran Anda dapat melihat usulan tindakan, tetapi tidak dapat menyetujui/menolak (memerlukan ai.approve)." />}
      {error && <ErrorNote text={error} />}
      {loading && <LoadingNote />}
      {!loading && (
        <>
          <ListToolbar
            resultCount={total}
            filters={
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'ALL' | AiProposal['status'])} style={{ width: 220 }}>
                <option value="ALL">Semua status</option>
                {Object.entries(PROPOSAL_STATUS_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </Select>
            }
          />
          {rows.length === 0 && <EmptyState text="Belum ada usulan tindakan dari AI." />}
          {rows.length > 0 && total === 0 && <EmptyState text="Tidak ada usulan yang cocok dengan filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Jenis</th>
                  <th style={thStyle}>Alasan</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Dibuat</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((p) => (
                  <ClickableRow key={p.proposal_id} onClick={() => setSelected(p)}>
                    <td style={tdStyle}>{ACTION_TYPE_LABEL[p.action_type]}</td>
                    <td style={{ ...tdStyle, fontSize: 11 }}>{p.reason}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={PROPOSAL_STATUS_LABEL[p.status]} />
                    </td>
                    <td style={tdStyle}>{formatDateTime(p.created_at)}</td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}

      <ProposalDrawer
        proposal={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        canApprove={canApprove}
        onDecided={() => void load()}
      />
    </div>
  );
}

function ProposalDrawer({
  proposal,
  open,
  onClose,
  canApprove,
  onDecided,
}: {
  proposal: AiProposal | null;
  open: boolean;
  onClose: () => void;
  canApprove: boolean;
  onDecided: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    setError(null);
    setReason('');
  }, [proposal?.proposal_id]);

  if (!proposal) return null;

  const decide = async (decision: 'APPROVE' | 'REJECT') => {
    setBusy(true);
    setError(null);
    try {
      await decideAiProposal(proposal.proposal_id, decision, reason.trim() || `${decision} via AI Analyst`);
      onDecided();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title={ACTION_TYPE_LABEL[proposal.action_type]}>
      <DrawerField label="Status" value={<StatusBadge status={PROPOSAL_STATUS_LABEL[proposal.status]} />} />
      <DrawerField label="Alasan (dari AI)" value={proposal.reason} />
      {proposal.decision_reason && <DrawerField label="Alasan Keputusan" value={proposal.decision_reason} />}
      {proposal.status === 'EXECUTED' && proposal.executed_reference && (
        <DrawerField label="Referensi Hasil Eksekusi" value={<code style={{ fontSize: 11 }}>{proposal.executed_reference}</code>} />
      )}
      {proposal.status === 'EXECUTION_FAILED' && (
        <ErrorNote text="Persetujuan tercatat, tetapi eksekusi ke fungsi domain nyata GAGAL (lihat alasan keputusan di atas) — tidak ada data yang berhasil diubah." />
      )}
      <DrawerField label="proposal_id" value={<code style={{ fontSize: 11 }}>{proposal.proposal_id}</code>} />

      {proposal.status === 'PENDING' && (
        <div style={{ marginTop: 14 }}>
          {canApprove ? (
            <>
              <FieldLabel label="Alasan Keputusan (opsional)">
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'var(--alex-control-height)',
                    padding: '0 10px',
                    border: '1px solid var(--alex-border)',
                    borderRadius: 'var(--alex-control-radius)',
                    fontSize: 'var(--alex-fs-caption)',
                    background: 'var(--alex-surface)',
                    fontFamily: 'var(--alex-font-family)',
                  }}
                />
              </FieldLabel>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <Button variant="primary" loading={busy} onClick={() => void decide('APPROVE')}>
                  Setujui & Eksekusi
                </Button>
                <Button variant="danger" loading={busy} onClick={() => void decide('REJECT')}>
                  Tolak
                </Button>
              </div>
              {error && <ErrorNote text={error} />}
            </>
          ) : (
            <HintNote text="Memerlukan izin ai.approve untuk menyetujui/menolak usulan ini." />
          )}
        </div>
      )}
    </Drawer>
  );
}

function EvalsTab() {
  const [evals, setEvals] = useState<AiEval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listAiEvals()
      .then(setEvals)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  const passCount = evals.filter((e) => e.last_status === 'PASS').length;

  return (
    <div>
      <p style={{ fontSize: 11.5, color: 'var(--alex-text-muted)', marginTop: 0 }}>
        Skenario keamanan yang diuji secara langsung terhadap database nyata (bukan hanya klaim dari kode) — mencakup pencegahan prompt
        injection, penolakan lintas cabang/tenant, larangan mutasi langsung, dan lainnya.
      </p>
      {error && <ErrorNote text={error} />}
      {loading && <LoadingNote />}
      {!loading && evals.length === 0 && <EmptyState text="Belum ada skenario uji yang tercatat." />}
      {!loading && evals.length > 0 && (
        <>
          <ListToolbar resultCount={evals.length} filters={<StatusBadge status={`${passCount}/${evals.length} LULUS`} />} />
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Kode</th>
                <th style={thStyle}>Kategori</th>
                <th style={thStyle}>Deskripsi</th>
                <th style={thStyle}>Hasil</th>
                <th style={thStyle}>Diuji</th>
              </tr>
            </thead>
            <tbody>
              {evals.map((e) => (
                <tr key={e.eval_id}>
                  <td style={tdStyle}>
                    <code style={{ fontSize: 11 }}>{e.eval_code}</code>
                  </td>
                  <td style={tdStyle}>{e.category}</td>
                  <td style={{ ...tdStyle, fontSize: 11 }}>{e.description}</td>
                  <td style={tdStyle}>{e.last_status ? <StatusBadge status={e.last_status === 'PASS' ? 'LULUS' : 'GAGAL'} /> : '—'}</td>
                  <td style={tdStyle}>{e.last_checked_at ? formatDateTime(e.last_checked_at) : 'Belum'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
